import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import {
  emailField,
  confirmEmailField,
  firstNameField,
  lastNameField,
  optionalFirstNameField,
  optionalLastNameField,
  phoneNumberField,
  confirmPhoneNumberField,
  optionalMessageField,
  selectOptionSchema,
} from "@core/schemas/fields";
import {
  withEmailConfirmation,
  withPhoneConfirmation,
} from "@core/schemas/refinements";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { PublicQuizQuestion } from "@platform/features/programQuiz";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { Type } from "@themes/pollinator/ui/Type";
import { useIntakeQuizTracking } from "../../marketing.analytics";
import { withLinks } from "../../utils/withLinks";
import {
  answerIssues,
  emptyAnswers as emptyAnswersFor,
  shownQuestions,
  type IntakeQuestion,
} from "./intakeQuestions";

/** What a container hands back once a submission is recorded. */
export type IntakeSubmissionResult = {
  /** The quiz response the answers were stored as */
  quizResponseId?: number | null;
};

/** Fields a form may ask for beyond the email address, which is always required. */
export type IntakeFieldName =
  | "first_name"
  | "last_name"
  | "phone_number"
  | "organization_name"
  | "self_reported_role"
  | "message";

/**
 * Whether a form asks for an organization and a role as a pair.
 *
 * When it asks for both, they are answered with the organization and role
 * selects the profile uses: the organization looked up from our records with
 * room to type one we do not have, the role chosen from a fixed list. A form
 * that words the organization field as a question wants a sentence, not a
 * lookup, so it keeps a text box; so does a form that asks for only one of
 * the two, since the selects come as a pair.
 */
export function asksForAffiliation(
  fields: IntakeFieldName[],
  fieldLabels?: Record<string, string>
) {
  return (
    fields.includes("organization_name") &&
    fields.includes("self_reported_role") &&
    !(fieldLabels?.organization_name ?? "").trim().endsWith("?")
  );
}

/**
 * Every field is present in the schema whatever the configuration says, and
 * the configuration decides only which of them are insisted on. A field the
 * form does not show stays optional and empty, which keeps the shape static
 * enough to type while still honouring what staff set in admin.
 */
export function buildIntakeSchema(
  requiredFields: IntakeFieldName[],
  extraFields: IntakeQuestion[] = [],
  { affiliation = false }: { affiliation?: boolean } = {}
) {
  const needs = (field: IntakeFieldName) => requiredFields.includes(field);
  // Answered through the paired selects, the organization and role arrive in
  // `affiliations` and are insisted on there rather than as text.
  const needsText = (field: IntakeFieldName) => needs(field) && !affiliation;
  const text = (max: number) => z.string().max(max, `Must be ${max} characters or less`);
  // A required answer of nothing but spaces is no answer; the server trims it
  // and refuses it, so it is refused here first, where the field can say why.
  const requiredText = (max: number, message: string) => text(max).trim().min(1, message);

  return withPhoneConfirmation(
    withEmailConfirmation(
      z.object({
        email: emailField,
        confirmEmail: confirmEmailField,
        firstName: needs("first_name") ? firstNameField : optionalFirstNameField,
        lastName: needs("last_name") ? lastNameField : optionalLastNameField,
        phoneNumber: needs("phone_number")
          ? phoneNumberField.refine((value) => !!value, {
              message: "Phone number is required",
            })
          : phoneNumberField,
        confirmPhoneNumber: confirmPhoneNumberField,
        organizationName: needsText("organization_name")
          ? requiredText(255, "Organization is required")
          : text(255).optional(),
        selfReportedRole: needsText("self_reported_role")
          ? requiredText(255, "Role is required")
          : text(255).optional(),
        // The same value the profile's affiliation field holds: a list of
        // organization and role pairs, of which a form asks for one.
        affiliations: z
          .array(
            z.object({
              organization: selectOptionSchema.nullish(),
              role: selectOptionSchema.nullish(),
            })
          )
          .default([]),
        message: needs("message")
          ? requiredText(2000, "Message is required")
          : optionalMessageField,
        // One key for every question this form adds. Keeping them together
        // leaves the built-in shape static enough to type, which a spread of
        // configured keys would not be.
        answers: z.record(z.unknown()).default({}),
      })
    )
  ).superRefine((data, ctx) => {
    if (affiliation) {
      const entry = data.affiliations?.[0];
      const missingOrganization =
        needs("organization_name") && !entry?.organization;
      const missingRole = needs("self_reported_role") && !entry?.role;
      // The pair is one field with one message under it, as it is on the
      // profile, so the message names whichever half is missing.
      const message =
        missingOrganization && missingRole
          ? "Organization and role are required"
          : missingOrganization
            ? "Organization is required"
            : missingRole
              ? "Role is required"
              : (entry?.organization?.label.length ?? 0) > 255
                ? "Must be 255 characters or less"
                : undefined;
      if (message) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["affiliations"],
          message,
        });
      }
    }

    // The form's own questions are its quiz's, so what is required, what is
    // allowed, and which of them are on screen at all are the quiz's rules.
    answerIssues(extraFields, data.answers).forEach(({ name, message }) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["answers", name],
        message,
      });
    });
  });
}

export type IntakeFormData = z.infer<ReturnType<typeof buildIntakeSchema>>;

/**
 * The organization and role as the server takes them.
 *
 * The names always travel as text, because that is what an inquiry records.
 * An id goes with a name only when it was picked from the list, so the server
 * can tell an organization we know from one somebody typed. Typing a name we
 * do not have creates nothing: it is submitted as the words it is.
 */
export function affiliationPayload(data: IntakeFormData) {
  const entry = data.affiliations?.[0];
  const idOf = (option?: { data?: { id?: unknown } } | null) =>
    typeof option?.data?.id === "number" ? option.data.id : undefined;

  return {
    organization_name: entry?.organization?.label ?? data.organizationName ?? "",
    organization_id: idOf(entry?.organization),
    self_reported_role: entry?.role?.label ?? data.selfReportedRole ?? "",
    affiliation_role_id: idOf(entry?.role),
  };
}

export type Confirmation = {
  heading: string;
  body: string;
};

export type Props = {
  /** Which configured form this is, recorded with every measurement event */
  formSlug?: string;
  /** Fields a submission must carry, as configured by staff */
  requiredFields: IntakeFieldName[];
  /** Fields offered but not insisted on */
  optionalFields: IntakeFieldName[];
  /** Wording overrides for the built-in fields, so a form can ask in its own words */
  fieldLabels?: Record<string, string>;
  /** Questions only this form asks */
  extraFields?: IntakeQuestion[];
  /** The form's id and its quiz's, recorded with every measurement event */
  formId?: number;
  quizId?: number;
  /** Called when the form is submitted with valid data */
  onSubmit: (data: IntakeFormData) => Promise<IntakeSubmissionResult | void>;
  /** Shown in place of the form once a submission succeeds */
  confirmation?: Confirmation;
  /** Custom class name for the form container */
  className?: string;
};

const DEFAULT_CONFIRMATION: Confirmation = {
  heading: "Thank you.",
  body: "We have your details and will be in touch.",
};

/**
 * The intake form the marketing site puts in front of every audience.
 *
 * Which fields appear, and which of them are required, is decided by staff in
 * admin rather than here, so one component serves applicants, funders and
 * organization admins alike. The email address is the exception: it identifies
 * the person, so it is always asked for and always required.
 *
 * Purely presentational. The container fetches the configuration and performs
 * the submission.
 */
export function MarketingIntakeForm({
  formSlug = "unknown",
  requiredFields,
  optionalFields,
  fieldLabels,
  extraFields,
  formId,
  quizId,
  onSubmit,
  confirmation,
  className,
}: Props) {
  const track = useIntakeQuizTracking({ quizId, formId, formSlug });
  const started = useRef(false);

  // Recorded once per form, however often the effect runs.
  const viewed = useRef<string>();
  useEffect(() => {
    if (viewed.current === formSlug) return;
    viewed.current = formSlug;
    track.loaded();
  }, [track, formSlug]);

  const handleSubmit = async (data: IntakeFormData) => {
    try {
      const result = await onSubmit(data);
      track.completed(result?.quizResponseId);
    } catch (error) {
      track.failed(error instanceof Error ? error.message : "unknown");
      throw error;
    }
  };
  const shown = useMemo(
    () => new Set<IntakeFieldName>([...requiredFields, ...optionalFields]),
    [requiredFields, optionalFields]
  );
  const isRequired = (field: IntakeFieldName) => requiredFields.includes(field);

  // Outlast's copy asks for the same answer in different words on each form,
  // so the wording is a setting with the built-in label as the fallback.
  const labelFor = (field: IntakeFieldName, fallback: string) =>
    fieldLabels?.[field] ?? fallback;

  // A relabelled field loses its hint. The built-in hints explain the built-in
  // wording, and read as a contradiction under a question that asks something
  // more specific.
  const placeholderFor = (field: IntakeFieldName, fallback: string) =>
    fieldLabels?.[field] ? undefined : fallback;

  const questions = useMemo(() => extraFields ?? [], [extraFields]);

  const schema = useMemo(
    () => buildIntakeSchema(requiredFields, questions),
    [requiredFields, questions]
  );

  // A question answered by choosing starts as an empty list, because that is
  // what the choice components hand back.
  const emptyAnswers = useMemo(() => emptyAnswersFor(questions), [questions]);

  const { form, handleSubmit: submitForm } = useForm({
    schema,
    onSubmit: handleSubmit,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      phoneNumber: "",
      confirmPhoneNumber: "",
      organizationName: "",
      selfReportedRole: "",
      message: "",
      answers: emptyAnswers,
    },
  });

  useEffect(() => {
    // The first keystroke separates someone who considered the form from
    // someone who only saw it, which is the drop worth knowing about.
    // Only a change somebody made counts. The form sets values of its own
    // when it loads, and counting those recorded everyone who saw the form
    // as having started it.
    const subscription = form.watch((_values, change) => {
      if (change.type !== "change" || started.current) return;
      started.current = true;
      track.started();
    });
    return () => subscription.unsubscribe();
  }, [form, track]);

  const { isSubmitting, isValid, isSubmitSuccessful } = form.formState;

  if (isSubmitSuccessful) {
    const { heading, body } = confirmation ?? DEFAULT_CONFIRMATION;
    return (
      <div className="flex flex-col border border-brand-800 rounded-lg p-6 gap-2">
        {/* Looks like an h4, but sits under the section's h2 in the outline. */}
        <Type variant="h4" as="h3">{heading}</Type>
        <Type variant="body2">{withLinks(body)}</Type>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Form form={form} className={className} innerClassName="justify-center">
        {shown.has("first_name") && (
          <FormField
            name="firstName"
            label={labelFor("first_name", "First Name")}
            component="Input"
            placeholder={placeholderFor("first_name", "Enter your first name")}
            required={isRequired("first_name")}
          />
        )}
        {shown.has("last_name") && (
          <FormField
            name="lastName"
            label={labelFor("last_name", "Last Name")}
            component="Input"
            placeholder={placeholderFor("last_name", "Enter your last name")}
            required={isRequired("last_name")}
          />
        )}
        <FormField
          name="email"
          label="Email"
          component="Input"
          placeholder="Enter your email"
          autoComplete="email"
          required
        />
        <FormField
          name="confirmEmail"
          label="Email Confirmation"
          component="Input"
          placeholder="Confirm your email"
          required
        />
        {shown.has("phone_number") && (
          <>
            <FormField
              name="phoneNumber"
              label={labelFor("phone_number", "Phone Number")}
              component="PhoneInput"
              placeholder={placeholderFor("phone_number", "Enter your phone number")}
              required={isRequired("phone_number")}
            />
            <FormField
              name="confirmPhoneNumber"
              label="Phone Number Confirmation"
              component="PhoneInput"
              placeholder="Confirm your phone number"
              required={isRequired("phone_number")}
            />
          </>
        )}
        {shown.has("organization_name") && (
          <FormField
            name="organizationName"
            label={labelFor("organization_name", "Organizational Affiliation")}
            component="Input"
            placeholder={placeholderFor("organization_name", "Where you work, if anywhere")}
            required={isRequired("organization_name")}
          />
        )}
        {shown.has("self_reported_role") && (
          <FormField
            name="selfReportedRole"
            label={labelFor("self_reported_role", "Your Role")}
            component="Input"
            placeholder={placeholderFor("self_reported_role", "Your role there")}
            required={isRequired("self_reported_role")}
          />
        )}
        {shown.has("message") && (
          <FormField
            name="message"
            label={labelFor("message", "Message")}
            component="Textarea"
            placeholder={placeholderFor("message", "Anything you would like us to know")}
            required={isRequired("message")}
          />
        )}
        {shownQuestions(questions, form.watch("answers")).map((field) => (
          <PublicQuizQuestion
            key={field.name}
            question={field}
            name={`answers.${field.name}`}
            helpText={field.help_text || undefined}
          />
        ))}
      </Form>
      <ActionButtonsGroup
        onPrimaryClick={submitForm}
        primaryButtonText="Submit"
        primaryButtonProps={{
          disabled: isSubmitting,
          visuallyDisabled: !isSubmitting && !isValid,
        }}
      />
    </div>
  );
}
